/**
 * /admin/plano-paginas — PLANO MASTER: Páginas da FEO no Lovable
 *
 * Camada 1 (topo): resumo direto — progresso, pendências e grade de status.
 * Camada 2 (abaixo): detalhes técnicos recolhidos (plano por item, inventário
 * de páginas e regras gerais).
 *
 * O status de cada item é editável e persistido em `site_config`.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  ExternalLink,
  LayoutTemplate,
  Link2 as LinkIcon,
  ListChecks,
  Target,
} from "lucide-react";

import { AdminGate } from "@/components/admin/AdminGate";
import { AdminCard, AdminSection, AdminShell, AdminStat } from "@/components/admin/shell";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  PAGINA_ITENS,
  TIPO_LABEL,
  defaultStatusMap,
  type ItemStatus,
} from "@/lib/planos/paginas-feo";
import {
  PAGINAS_PRONTAS,
  PLANO_POR_ITEM,
  SITE,
} from "@/lib/planos/paginas-feo-plano";
import {
  getPlanoPaginasStatus,
  setPlanoPaginaStatus,
} from "@/lib/planos/paginas-feo.functions";

export const Route = createFileRoute("/admin/plano-paginas")({
  head: () => ({
    meta: [
      { title: "Admin — Páginas da FEO no Lovable" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/plano-paginas">
      <PlanoPaginasPage />
    </AdminGate>
  ),
});

const REGRAS = [
  {
    t: "Pós-graduações — com captura",
    d: "LP completa (venda) + LP resumida (lead/ementa). Ementa em PDF e pop-up pré-checkout enviam o lead pro RD Station + RD CRM (FUNIL-POS; universitário vai pra UNIVERSITÁRIOS). Pré-checkout entra como quente.",
  },
  {
    t: "Demais produtos — sem captura",
    d: "Star, certificações, AprovaFEO e Agente IA não têm ementa nem pop-up: o botão Comprar vai direto pro checkout Eduzz. Nenhum desses leads entra no CRM.",
  },
  {
    t: "Versão Google (-gg)",
    d: "Catálogos e pré-checkouts têm espelho para Google Ads com cupom próprio. Links vivem no gerenciador em /admin/painel e nas campanhas.",
  },
  {
    t: "Padrão visual",
    d: "Um único padrão de LP reaproveitado (hero, blocos, prova social, FAQ, CTA). Sem variação por designer; só conteúdo e imagens mudam por produto.",
  },
  {
    t: "Teste A/B de LP",
    d: "Quando há duas versões da mesma página (ex.: /star e /star2, /orto1 e /orto2), só a vencedora fica indexável — a outra recebe noindex pra não duplicar conteúdo.",
  },
];

function RouteChip({ path }: { path: string }) {
  return (
    <a
      href={`${SITE}${path}`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 rounded bg-white/5 px-1.5 py-0.5 font-mono text-[11px] text-white/70 transition-colors hover:bg-white/10 hover:text-white"
    >
      {path}
      <ExternalLink className="h-2.5 w-2.5" />
    </a>
  );
}

function PlanoPaginasPage() {
  const [status, setStatus] = useState<Record<string, ItemStatus>>(() => defaultStatusMap());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setStatus(await getPlanoPaginasStatus());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao carregar checklist");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (id: string) => {
    const next: ItemStatus = status[id] === "finalizado" ? "pendente" : "finalizado";
    setStatus((prev) => ({ ...prev, [id]: next }));
    try {
      await setPlanoPaginaStatus({ data: { id, status: next } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao salvar");
      setStatus((prev) => ({ ...prev, [id]: next === "finalizado" ? "pendente" : "finalizado" }));
    }
  };

  const prontas = useMemo(
    () => PAGINA_ITENS.filter((i) => status[i.id] === "finalizado"),
    [status],
  );
  const pendentes = useMemo(
    () => PAGINA_ITENS.filter((i) => status[i.id] !== "finalizado"),
    [status],
  );
  const pct = Math.round((prontas.length / PAGINA_ITENS.length) * 100);

  return (
    <AdminShell
      title="Páginas da FEO no Lovable"
      icon={<LayoutTemplate className="h-5 w-5 text-admin-accent" />}
      description="Plano master de criação e migração de todas as páginas da operação — institucionais, catálogos, LPs de venda, funis de captura e blog."
      actions={
        <Badge variant="secondary" className="text-[10px] uppercase tracking-widest">
          Em andamento
        </Badge>
      }
    >
      {/* ---------------------------------------------------------- resumo */}
      <AdminSection label="Resumo">
        <div className="grid gap-3 sm:grid-cols-3">
          <AdminStat label="Prontas" value={prontas.length} hint="frentes concluídas" />
          <AdminStat label="Pendentes" value={pendentes.length} hint="ainda em execução" />
          <AdminStat label="Progresso" value={`${pct}%`} hint={`${prontas.length}/${PAGINA_ITENS.length} frentes`} />
        </div>
        <AdminCard className="py-4">
          <Progress value={pct} className="h-2" />
        </AdminCard>
      </AdminSection>

      {/* ------------------------------------------------------ pendências */}
      <AdminSection label="Pendências agora">
        {loading ? (
          <AdminCard>
            <p className="text-sm text-white/55">Carregando...</p>
          </AdminCard>
        ) : pendentes.length === 0 ? (
          <AdminCard>
            <p className="text-sm text-white/70">
              Nenhuma pendência: todas as frentes de página estão finalizadas.
            </p>
          </AdminCard>
        ) : (
          <div className="space-y-2">
            {pendentes.map((item) => (
              <AdminCard key={item.id} className="p-4 sm:p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-white">{item.label}</span>
                  <Badge variant="outline" className="text-[10px]">
                    {TIPO_LABEL[item.tipo]}
                  </Badge>
                </div>
                {item.proximaAcao && (
                  <p className="mt-2 flex gap-2 text-xs leading-relaxed text-white/70">
                    <Target className="mt-0.5 h-3.5 w-3.5 shrink-0 text-admin-accent" />
                    <span>{item.proximaAcao}</span>
                  </p>
                )}
                {item.rotas && item.rotas.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {item.rotas.map((p) => (
                      <RouteChip key={p} path={p} />
                    ))}
                  </div>
                )}
              </AdminCard>
            ))}
          </div>
        )}
      </AdminSection>

      {/* --------------------------------------------------- grade de status */}
      <AdminSection label="Status por frente">
        <AdminCard>
          <div className="grid gap-2 sm:grid-cols-2">
            {PAGINA_ITENS.map((item) => {
              const isDone = status[item.id] === "finalizado";
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => void toggle(item.id)}
                  className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                    isDone
                      ? "border-emerald-400/25 bg-emerald-400/5 hover:bg-emerald-400/10"
                      : "border-amber-400/25 bg-amber-400/5 hover:bg-amber-400/10"
                  }`}
                >
                  {isDone ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
                  ) : (
                    <Circle className="h-4 w-4 shrink-0 text-amber-400" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">
                    {item.label}
                  </span>
                  <span className="shrink-0 text-[10px] uppercase tracking-wider text-white/40">
                    {TIPO_LABEL[item.tipo]}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-white/45">
            Clique em qualquer frente para alternar entre finalizado e pendente — o status fica
            salvo.
          </p>
        </AdminCard>
      </AdminSection>

      {/* ------------------------------------------- camada 2 · detalhes */}
      <AdminSection label="Detalhes técnicos">
        <AdminCard>
          <p className="text-xs text-white/50">
            Tudo o que é detalhe fica recolhido aqui: situação de cada frente, plano de execução
            passo a passo, inventário de páginas e regras gerais.
          </p>
          <Accordion type="single" collapsible className="mt-4 w-full">
            {/* situação de cada frente */}
            <AccordionItem value="situacao">
              <AccordionTrigger className="text-left text-sm font-semibold">
                Situação de cada frente ({PAGINA_ITENS.length})
              </AccordionTrigger>
              <AccordionContent className="space-y-2">
                {PAGINA_ITENS.map((item) => {
                  const isDone = status[item.id] === "finalizado";
                  return (
                    <div
                      key={item.id}
                      className="rounded-lg border border-white/10 bg-white/[0.03] p-3"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-white">{item.label}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {TIPO_LABEL[item.tipo]}
                        </Badge>
                        <Badge
                          variant={isDone ? "default" : "secondary"}
                          className="text-[10px] uppercase"
                        >
                          {isDone ? "finalizado" : "pendente"}
                        </Badge>
                      </div>
                      {item.nota && (
                        <p className="mt-1.5 text-xs leading-relaxed text-white/60">{item.nota}</p>
                      )}
                      {item.rotas && item.rotas.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {item.rotas.map((p) => (
                            <RouteChip key={p} path={p} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </AccordionContent>
            </AccordionItem>

            {/* plano de execução por item */}
            {PLANO_POR_ITEM.map((plano) => {
              const item = PAGINA_ITENS.find((i) => i.id === plano.id);
              const isDone = status[plano.id] === "finalizado";
              return (
                <AccordionItem key={plano.id} value={plano.id}>
                  <AccordionTrigger className="text-left">
                    <span className="flex flex-1 flex-wrap items-center gap-2 pr-2">
                      <ListChecks className="h-3.5 w-3.5 text-admin-accent" />
                      <span className="text-sm font-semibold">
                        Plano · {item?.label ?? plano.id}
                      </span>
                      <Badge
                        variant={isDone ? "default" : "secondary"}
                        className="text-[10px] uppercase"
                      >
                        {isDone ? "finalizado" : "pendente"}
                      </Badge>
                      <span className="text-[11px] text-white/45">
                        {plano.etapas.length} passos
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <p className="text-sm leading-relaxed text-white/65">{plano.objetivo}</p>

                    <div>
                      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/45">
                        Entregáveis
                      </p>
                      <ul className="space-y-1">
                        {plano.entregaveis.map((e) => (
                          <li key={e} className="flex gap-2 text-xs text-white/70">
                            <Target className="mt-0.5 h-3 w-3 shrink-0 text-admin-accent" />
                            <span>{e}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <ol className="space-y-2">
                      {plano.etapas.map((et, i) => (
                        <li
                          key={et.titulo}
                          className="flex gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3"
                        >
                          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-admin-accent/15 text-xs font-bold text-admin-accent">
                            {i + 1}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-white">{et.titulo}</p>
                            <p className="mt-1 text-xs leading-relaxed text-white/60">
                              {et.detalhe}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ol>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/45">
                          Reaproveitar
                        </p>
                        <ul className="space-y-1 text-xs text-white/60">
                          {plano.reaproveitar.map((r) => (
                            <li key={r}>· {r}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="rounded-lg border border-destructive/25 bg-destructive/5 p-3">
                        <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/45">
                          <AlertTriangle className="h-3 w-3" /> Cuidados
                        </p>
                        <ul className="space-y-1 text-xs text-white/60">
                          {plano.cuidados.map((c) => (
                            <li key={c}>· {c}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {plano.pronto.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] uppercase tracking-wider text-white/45">
                          Já no ar:
                        </span>
                        {plano.pronto.map((p) => (
                          <RouteChip key={p} path={p} />
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}

            {/* inventário */}
            <AccordionItem value="inventario">
              <AccordionTrigger className="text-left text-sm font-semibold">
                <span className="flex items-center gap-2">
                  <LinkIcon className="h-3.5 w-3.5 text-admin-accent" /> Inventário de páginas já
                  criadas
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-4">
                {PAGINAS_PRONTAS.map((g) => (
                  <div key={g.grupo}>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-admin-accent">
                      {g.grupo}
                    </p>
                    <p className="mb-2 text-xs text-white/50">{g.resumo}</p>
                    <div className="grid gap-1.5 sm:grid-cols-2">
                      {g.paginas.map((p) => (
                        <a
                          key={p.path}
                          href={`${SITE}${p.path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5 transition-colors hover:bg-white/[0.06]"
                        >
                          <span className="flex items-center gap-1.5 text-sm font-semibold text-white">
                            {p.label} <ExternalLink className="h-3 w-3 text-white/40" />
                          </span>
                          <span className="mt-0.5 block font-mono text-[11px] text-white/45">
                            {p.path}
                          </span>
                          {p.nota && (
                            <span className="mt-0.5 block text-xs text-white/55">{p.nota}</span>
                          )}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>

            {/* regras */}
            <AccordionItem value="regras">
              <AccordionTrigger className="text-left text-sm font-semibold">
                Regras que valem pra todas as páginas
              </AccordionTrigger>
              <AccordionContent className="space-y-3">
                {REGRAS.map((r) => (
                  <div
                    key={r.t}
                    className="rounded-lg border border-white/10 bg-white/[0.03] p-3"
                  >
                    <p className="text-sm font-semibold text-white">{r.t}</p>
                    <p className="mt-1 text-xs leading-relaxed text-white/60">{r.d}</p>
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/mapa">Mapa da operação</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/painel">Links &amp; checkouts</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/contexto">Contexto</Link>
            </Button>
          </div>
        </AdminCard>
      </AdminSection>

      <footer className="py-6 text-center text-xs text-white/35">
        /admin/plano-paginas · plano master das páginas
      </footer>
    </AdminShell>
  );
}
